<template>
  <InputSkeleton :name="name"
                 :errors="errors"
                 class="DatePeriodInput"
                 :class="{
                   [`DatePeriodInput--${size}`]: !!size,
                   'DatePeriodInput--disabled': disabled,
                   'DatePeriodInput--readonly': readonly,
                   'DatePeriodInput--invalid': isInvalid,
                 }">
    <template v-if="$slots.default || label" #label>
      <slot v-if="$slots.default" />
      <template v-else>{{ label }}</template>
    </template>
    <div class="DatePeriodInput__input">
      <button type="button"
              class="DatePeriodInput__button"
              :disabled="isPrevDisabled"
              data-test-id="prev"
              @click="onPrevWeek">
        <Icon name="arrow.left" />
      </button>
      <div class="DatePeriodInput__preview">{{ minPreview }} — {{ maxPreview }}</div>
      <button type="button"
              class="DatePeriodInput__button"
              :disabled="isNextDisabled"
              data-test-id="next"
              @click="onNextWeek">
        <Icon name="arrow.right" />
      </button>
    </div>
    <input :value="innerMinValue.toISOString()" type="hidden" :name="`${name}[min]`" />
    <input :value="innerMaxValue.toISOString()" type="hidden" :name="`${name}[max]`" />
  </InputSkeleton>
</template>

<script setup lang="ts">
  import dayjs, { Dayjs } from 'dayjs';
  import { isEqual } from 'lodash';
  import { computed, onBeforeMount, ref } from 'vue';
  import { UiSize } from '../../../constants';
  import Icon from '../../Icon.vue';
  import InputSkeleton from '../InputSkeleton.vue';
  import { InputErrorObject } from './types';

  const props = withDefaults(defineProps<{
    min: string;
    max: string;
    name: string;
    size?: UiSize;
    label?: string;
    disabled?: boolean;
    readonly?: boolean;
    invalid?: boolean;
    errors?: InputErrorObject[];
    minDate?: string;
    maxDate?: string;
    unit?: 'week' | 'month';
  }>(), {
    size: UiSize.MD,
    errors: () => [],
    unit: 'week',
  });

  const emit = defineEmits<{
    (e: 'update:min', value: string): void;
    (e: 'update:max', value: string): void;
    (e: 'update:model-value', minValue: string, maxValue: string): void;
  }>();

  const innerMinValue = ref<Dayjs>(null);
  const innerMaxValue = ref<Dayjs>(null);

  onBeforeMount(() => {
    innerMinValue.value = dayjs(props.min);
    innerMaxValue.value = dayjs(props.max);
  });

  const FORMAT_SHORT = 'DD MMM';
  const FORMAT_LONG = 'DD MMM YYYY';

  const minPreview = computed((): string => innerMinValue.value.format(
    innerMinValue.value.isSame(innerMaxValue.value, 'year') ? FORMAT_SHORT : FORMAT_LONG,
  ));
  const maxPreview = computed((): string => innerMaxValue.value.format(FORMAT_LONG));

  const prevMinValue = computed(() => dayjs(props.min).add(-1, props.unit).startOf(props.unit));
  const prevMaxValue = computed(() => prevMinValue.value.clone().endOf(props.unit));
  const isPrevDisabled = computed(
    (): boolean => Boolean(props.disabled || (props.minDate && prevMinValue.value.isBefore(props.minDate, props.unit))),
  );

  const nextMinValue = computed(() => dayjs(props.min).add(1, props.unit).startOf(props.unit));
  const nextMaxValue = computed(() => nextMinValue.value.clone().endOf(props.unit));
  const isNextDisabled = computed(
    (): boolean => Boolean(props.disabled || (props.maxDate && nextMaxValue.value.isAfter(props.maxDate, props.unit))),
  );

  const isInvalid = computed<boolean>(() => Boolean(props.invalid || props.errors.length));

  const onPrevWeek = () => {
    if (props.minDate && prevMinValue.value.isBefore(props.minDate)) {
      innerMinValue.value = dayjs(props.minDate);
      innerMaxValue.value = innerMinValue.value.clone().endOf(props.unit);
    } else {
      innerMinValue.value = prevMinValue.value;
      innerMaxValue.value = prevMaxValue.value;
    }

    const isNotChanged = isEqual(
      { min: props.min, max: props.max },
      { min: innerMinValue.value.toISOString(), max: innerMaxValue.value.toISOString() },
    );

    if (isNotChanged) {
      return;
    }

    emit('update:min', innerMinValue.value.toISOString());
    emit('update:max', innerMaxValue.value.toISOString());
    emit('update:model-value', innerMinValue.value.toISOString(), innerMaxValue.value.toISOString());
  };

  const onNextWeek = () => {
    if (props.maxDate && nextMaxValue.value.isAfter(props.maxDate)) {
      innerMaxValue.value = dayjs(props.maxDate);
      innerMinValue.value = innerMaxValue.value.clone().startOf(props.unit);
    } else {
      innerMinValue.value = nextMinValue.value;
      innerMaxValue.value = nextMaxValue.value;
    }

    const isNotChanged = isEqual(
      { min: props.min, max: props.max },
      { min: innerMinValue.value.toISOString(), max: innerMaxValue.value.toISOString() },
    );

    if (isNotChanged) {
      return;
    }

    emit('update:min', innerMinValue.value.toISOString());
    emit('update:max', innerMaxValue.value.toISOString());
    emit('update:model-value', innerMinValue.value.toISOString(), innerMaxValue.value.toISOString());
  };
</script>

<style lang="scss" scoped>
  @import '../../../assets/scss/_variables.scss';
  @import '../../../assets/scss/_mixins.scss';

  .DatePeriodInput {
    border-radius: 4px;

    &--disabled {
      // ...
    }

    &__input {
      display: flex;
      grid-template-columns: max-content auto max-content;
      justify-content: space-between;
      align-items: center;
      column-gap: 15px;
    }

    &--md &__input {
      height: 30px;
    }

    &--lg &__input {
      height: 40px;
    }

    &--xl &__input {
      height: 50px;
    }

    &__button {
      @include transition($transition-function, $transition-duration, background-color);

      display: flex;
      justify-content: center;
      align-items: center;
      width: 20px;
      height: 20px;
      padding: 0;
      background-color: transparent;
      border: none;
      cursor: pointer;

      &:disabled {
        cursor: not-allowed;

        & svg {
          color: $color-grey-100;
        }
      }

      svg {
        @include transition($transition-function, $transition-duration, color);

        width: 21px;
        height: 24px;
        color: $color-second;
      }

      &:hover svg {
        color: $color-red-100;
      }
    }

    &--disabled &__button {
      opacity: 0.5;
      cursor: not-allowed;
    }

    &__preview {
      font-size: 16px;
      line-height: 19px;
      white-space: nowrap;
    }

    &--disabled &__preview {
      opacity: 0.5;
    }
  }
</style>
